/**
 * Clientes API Service
 * CRUD operations for client management
 */

import { api } from "@/lib/api";

const CLIENTES_URL = "/clientes/";

// =============================================================================
// CONSTANTS
// =============================================================================

export const TIPOS_CLIENTE = [
  { value: "AREA_HOSPITALARIA", label: "Área Hospitalaria" },
  { value: "EMPRESA", label: "Empresa" },
  { value: "PARTICULAR", label: "Particular" },
] as const;

export type TipoCliente = (typeof TIPOS_CLIENTE)[number]["value"];

export function getTipoClienteLabel(tipo: string | undefined | null): string {
  if (!tipo) return "";
  const found = TIPOS_CLIENTE.find((t) => t.value === tipo);
  return found?.label || tipo;
}

// =============================================================================
// TYPES
// =============================================================================

export interface Cliente {
  id: number;
  nombre: string;
  tipo: TipoCliente;
  tipo_display: string;
  codigo: string;
  rtn: string;
  contacto_nombre: string;
  contacto_telefono: string;
  contacto_email: string;
  direccion: string;
  sucursal: number;
  sucursal_nombre: string;
  lista_precio: number | null;
  lista_precio_nombre: string | null;
  observaciones: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClienteListItem {
  id: number;
  nombre: string;
  tipo: TipoCliente;
  tipo_display: string;
  codigo: string;
  rtn: string;
  contacto_nombre: string;
  contacto_telefono: string;
  lista_precio: number | null;
  is_active: boolean;
}

export interface ClienteCreateData {
  nombre: string;
  tipo?: TipoCliente;
  codigo?: string;
  rtn?: string;
  contacto_nombre?: string;
  contacto_telefono?: string;
  contacto_email?: string;
  direccion?: string;
  sucursal: number;
  lista_precio?: number | null;
  observaciones?: string;
}

export type ClienteUpdateData = Partial<ClienteCreateData>;

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ClienteFilters {
  search?: string;
  tipo?: string;
  is_active?: boolean | string;
  sucursal?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

export interface FetchParams {
  page: number;
  page_size: number;
  ordering?: string;
  search?: string;
  tipo?: string;
  is_active?: string;
  [key: string]: string | number | boolean | undefined;
}

export async function getClientes(
  params: FetchParams | ClienteFilters = {}
): Promise<PaginatedResponse<ClienteListItem>> {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.append("search", String(params.search));
  if (params.ordering) searchParams.append("ordering", String(params.ordering));
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));
  if (params.tipo) searchParams.append("tipo", String(params.tipo));

  if (params.is_active !== undefined && params.is_active !== "all") {
    const isActiveValue =
      typeof params.is_active === "boolean" ? params.is_active : params.is_active === "true";
    searchParams.append("is_active", String(isActiveValue));
  }

  const url = searchParams.toString() ? `${CLIENTES_URL}?${searchParams}` : CLIENTES_URL;
  const response = await api.get<PaginatedResponse<ClienteListItem>>(url);
  return response.data;
}

export async function getCliente(id: number): Promise<Cliente> {
  const response = await api.get<Cliente>(`${CLIENTES_URL}${id}/`);
  return response.data;
}

export async function createCliente(data: ClienteCreateData): Promise<Cliente> {
  const response = await api.post<Cliente>(CLIENTES_URL, data);
  return response.data;
}

export async function updateCliente(id: number, data: ClienteUpdateData): Promise<Cliente> {
  const response = await api.patch<Cliente>(`${CLIENTES_URL}${id}/`, data);
  return response.data;
}

export async function deleteCliente(id: number): Promise<void> {
  await api.delete(`${CLIENTES_URL}${id}/`);
}

export async function activateCliente(id: number): Promise<Cliente> {
  const response = await api.post<Cliente>(`${CLIENTES_URL}${id}/activate/`);
  return response.data;
}

export async function deactivateCliente(id: number): Promise<Cliente> {
  const response = await api.post<Cliente>(`${CLIENTES_URL}${id}/deactivate/`);
  return response.data;
}

export async function buscarClientes(query: string, limit = 20): Promise<ClienteListItem[]> {
  const response = await api.get<ClienteListItem[]>(`${CLIENTES_URL}buscar/`, {
    params: { search: query, limit },
  });
  return response.data;
}
